package pl.mothdigital.neuronowi.components.widgets

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.navigation.*
import com.varabyte.kobweb.silk.components.text.*
import com.varabyte.kobweb.silk.style.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import pl.mothdigital.neuronowi.theme.*

/**
 * Represents a single breadcrumb item.
 *
 * @param label Display text
 * @param path Route path (null for current page)
 */
data class BreadcrumbItem(
    val label: String,
    val path: String? = null
)

val BreadcrumbsContainerStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(topBottom = 0.5.cssRem, leftRight = 6.percent)
            .fontSize(0.875.cssRem)
    }
}

val BreadcrumbLinkStyle = CssStyle {
    base {
        Modifier
            .color(SiteTheme.green._800)
            .textDecorationLine(TextDecorationLine.None)
    }

    cssRule(":hover") {
        Modifier.textDecorationLine(TextDecorationLine.Underline)
    }
}

/**
 * Breadcrumb navigation component for better UX and SEO.
 *
 * Example usage:
 * ```
 * Breadcrumbs(
 *     items = listOf(
 *         BreadcrumbItem("Strona główna", "/"),
 *         BreadcrumbItem("Specjaliści", "/specialists/"),
 *         BreadcrumbItem("Magdalena Nowik")
 *     )
 * )
 * ```
 *
 * @param items List of breadcrumb items (last item is current page)
 */
@Composable
fun Breadcrumbs(items: List<BreadcrumbItem>) {
    if (items.size <= 1) return // Don't show breadcrumbs for home page only

    Nav(attrs = BreadcrumbsContainerStyle.toModifier().ariaLabel("Breadcrumb").toAttrs()) {
        Row(
            modifier = Modifier.gap(0.5.cssRem).flexWrap(FlexWrap.Wrap),
            verticalAlignment = Alignment.CenterVertically
        ) {
            items.forEachIndexed { index, item ->
                if (index > 0) {
                    SpanText(
                        text = "/",
                        modifier = Modifier.color(SiteTheme.gray._600).padding(leftRight = 0.25.cssRem)
                    )
                }

                if (item.path != null && index < items.size - 1) {
                    // Clickable breadcrumb
                    Link(
                        path = item.path,
                        text = item.label,
                        modifier = BreadcrumbLinkStyle.toModifier(),
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant)
                    )
                } else {
                    // Current page (not clickable)
                    SpanText(
                        text = item.label,
                        modifier = Modifier.color(SiteTheme.gray._800).fontWeight(FontWeight.Medium)
                    )
                }
            }
        }
    }
}
